/**
 * :func:`SystemStatusStreamProvider` — the **application-scope** owner of the
 * one ``GET /api/stream/system-status`` EventSource (REQ-STATUS-048,
 * REQ-STATUS-062).
 *
 * Deliberately shaped as the twin of :file:`@/map/sse-provider`: the ``.tsx``
 * module exports the component exclusively, while the context value and the
 * access hooks live in the ``.ts`` sibling
 * (:file:`@/system-status/use-system-status-stream`). Same fast-refresh
 * discipline, same mounting position — above :class:`RouterProvider` in
 * :file:`main.tsx`, so both the chrome (the top bar's system rollup badge) and
 * the routed panels (the System panel, the User Interface Manager Selection
 * Detail) read one connection.
 *
 * # Why the stream had to leave the panel
 *
 * It used to be owned by :class:`SystemStatusPanel` and torn down on unmount,
 * and :func:`useSystemStatusSnapshot` has no ``refetchInterval`` because
 * freshness was the stream's job. A rollup badge in always-visible chrome built
 * on that plumbing freezes at its first value the moment the panel is closed —
 * a permanently green pill over a dead feed, the false green REQ-STATUS-016
 * exists to prevent. The panel is now a reader
 * (:func:`useSystemStatusStreamConnection`) like every other consumer.
 *
 * Accepted cost, stated in REQ-STATUS-048: one always-on EventSource per
 * authenticated tab at roughly one frame per five seconds.
 *
 * # Bounds (REQ-STATUS-062)
 *
 * - **One connection per tab regardless of subscriber count** — subscribers read
 *   context; only this component calls :func:`useSystemStatusStream`
 *   (REQ-UI-035: no second stream, and no non-BFF call).
 * - **No reconnect on 401/403** — the hook reports ``closed`` when the browser
 *   stops retrying and does not re-open on its own; ``resetKey`` (the subject
 *   id) re-opens only once a bootstrap succeeds.
 * - **Closed on session end** — :class:`SessionProvider` renders children only
 *   while a session is bootstrapped, so an ended session unmounts this provider
 *   and the hook's cleanup closes the EventSource.
 *
 * Cross-references:
 *   - REQ-STATUS-048 (app-scope hoist); REQ-STATUS-062 (bounded always-on stream).
 *   - REQ-STATUS-016 (never a false green); REQ-STATUS-029 / REQ-PEER-042 (RBAC).
 *   - REQ-UI-035 (BFF-only, one stream); ADR-016 §1/§3 (snapshot + SSE).
 */
import type { ReactNode } from "react";
import { SYSTEM_STATUS_READER_ROLES } from "@/system-status/use-system-status-snapshot";
import {
  SystemStatusStreamContext,
  useSystemStatusStream,
} from "@/system-status/use-system-status-stream";
import { useSession } from "@/auth/session-context";

/**
 * Props for :func:`SystemStatusStreamProvider`.
 *
 * Parameter-free in production; tests inject a fake :class:`EventSource`.
 */
export interface SystemStatusStreamProviderProps {
  /** :class:`EventSource` factory override — tests inject a fake. */
  eventSourceFactory?: (url: string) => EventSource;
  /** Endpoint override (tests). */
  endpoint?: string;
  children: ReactNode;
}

/**
 * Hold the one system-status SSE connection for the tab's session and publish it
 * to descendants.
 *
 * The role gate mirrors :func:`useSystemStatusSnapshot`'s: every authenticated
 * Meridian role reads this surface, so in practice the gate only closes for a
 * session carrying no known role (the BFF returns 403 for the same session).
 * Gating the stream and not the snapshot — or the reverse — would leave a role
 * on a first paint that silently went stale, so the two must move together.
 */
export function SystemStatusStreamProvider({
  eventSourceFactory,
  endpoint,
  children,
}: SystemStatusStreamProviderProps): React.ReactElement {
  const session = useSession();
  const connection = useSystemStatusStream({
    enabled: session.hasAnyRole(SYSTEM_STATUS_READER_ROLES),
    eventSourceFactory,
    endpoint,
    // Re-open only when the authenticated identity changes — i.e. after a
    // successful re-bootstrap. A stream the BFF rejected with 401/403 stays
    // ``closed`` until then (REQ-STATUS-062).
    resetKey: session.user.subject_id,
  });

  return (
    <SystemStatusStreamContext.Provider value={connection}>
      {children}
    </SystemStatusStreamContext.Provider>
  );
}
